import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const b64ToUint8 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map(c => c.charCodeAt(0))); };
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export function usePush() {
  const [state, setState] = useState({ supported: supported(), enabled: false, subscribed: false, permission: typeof Notification !== "undefined" ? Notification.permission : "default", busy: false });

  const refresh = useCallback(async () => {
    if (!supported()) return;
    try {
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setState(s => ({ ...s, enabled: data.enabled, subscribed: !!sub, permission: Notification.permission }));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = async () => {
    if (!supported()) return toast.error("Push isn't supported in this browser");
    setState(s => ({ ...s, busy: true }));
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications were blocked", { description: "Allow notifications for this site in browser settings." }); return; }
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
      const j = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, user_agent: navigator.userAgent });
      await api.post("/push/test");
      toast.success("Push alerts enabled on this device");
    } catch (e) { toast.error("Could not enable push", { description: e.response?.data?.detail || e.message }); }
    finally { setState(s => ({ ...s, busy: false })); refresh(); }
  };

  const unsubscribe = async () => {
    setState(s => ({ ...s, busy: true }));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      toast.success("Push alerts turned off on this device");
    } catch (e) { toast.error("Could not turn off push", { description: e.message }); }
    finally { setState(s => ({ ...s, busy: false })); refresh(); }
  };

  return { ...state, subscribe, unsubscribe };
}
